import { cn } from "./classNames-DkX30VJU.js";
import { ChevronDown, ChevronRight, Ellipsis } from "./icons-DDR9dFSr.js";
import { Popover } from "./Popover-DhOPlWgF.js";
import { ToggleAllTooltip, isToggleAllClick } from "./ToggleAllTooltip-EghNnsQC.js";
import { SearchInput } from "./SearchInput-BTTnMdb1.js";
import { Children, createContext, isValidElement, use, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Slot } from "@radix-ui/react-slot";
import { jsx, jsxs } from "react/jsx-runtime";
// src/components/Breadcrumb/BreadcrumbDropdownContext.ts
const NO_TOGGLES = {};
/** Records `toggle` under `scope`, keeping the toggles made without a search. */
const withToggle = (toggles, scope, toggle) => ({
	...toggles[""] && { "": toggles[""] },
	[scope]: toggle
});
const BreadcrumbDropdownContext = createContext(null);
const matchesQuery = (text, query) => text.toLowerCase().includes(query);
/** Text content of a React tree — the default filter text when no `textValue` is given. */
const getNodeText = (node) => {
	if (typeof node === "string" || typeof node === "number") return String(node);
	if (Array.isArray(node)) return node.map(getNodeText).join("");
	if (isValidElement(node)) return getNodeText(node.props.children);
	return "";
};
const normalizeQuery = (query) => query.trim().toLowerCase();
/**
* Visibility registry for one filtering scope (the panel root, or one group). Items and groups
* report their visibility here; `anyVisible` drives the group header / empty-state rendering.
*/
const useMatchRegistry = () => {
	const [matches, setMatches] = useState(/* @__PURE__ */ new Map());
	return {
		register: useCallback((id, visible) => {
			setMatches((prev) => prev.get(id) === visible ? prev : new Map(prev).set(id, visible));
			return () => {
				setMatches((prev) => {
					if (!prev.has(id)) return prev;
					const next = new Map(prev);
					next.delete(id);
					return next;
				});
			};
		}, []),
		anyVisible: useMemo(() => [...matches.values()].some(Boolean), [matches])
	};
};
// src/components/Breadcrumb/BreadcrumbDropdownGroup.tsx
const headerClass = cn("tw:flex", "tw:w-full", "tw:items-center", "tw:gap-2", "tw:box-border", "tw:rounded-md", "tw:border-none", "tw:bg-transparent", "tw:px-2", "tw:py-1.5", "tw:text-left", "tw:text-sm", "tw:font-medium", "tw:text-foreground", "tw:min-w-0", "tw:overflow-hidden", "tw:[&_svg:not([class*='tw:size-'])]:size-4", "tw:[&_svg]:shrink-0");
const collapsibleHeaderClass = cn("tw:cursor-pointer", "tw:hover:bg-hover", "tw:focus-visible:outline-hidden", "tw:focus-visible:ring-2", "tw:focus-visible:ring-ring/50");
/**
* A labelled group of `Breadcrumb.Dropdown.Item`s (e.g. one bucket with its tables). The group
* hides itself when neither its label nor any child matches the filter query; an active query
* opens collapsible groups, and a fold made under it lasts until the query changes. Non-empty `collapsible` groups toggle their items behind the
* header with an animated expand / collapse; a `collapsible` group with no items renders a
* non-interactive, greyed header that can't toggle.
*/
const BreadcrumbDropdownGroup = ({ label, textValue, collapsible = false, defaultExpanded = true, className, children }) => {
	const parent = use(BreadcrumbDropdownContext);
	if (!parent) throw new Error("Breadcrumb.Dropdown.Group must be rendered inside Breadcrumb.Dropdown");
	const id = useId();
	const { register: registerChild, anyVisible } = useMatchRegistry();
	const [own, setOwn] = useState(NO_TOGGLES);
	const text = textValue ?? getNodeText(label);
	const filtering = !parent.filterDisabled && !!parent.query;
	const scope = filtering ? parent.query : "";
	const ownToggle = own[scope];
	const allToggle = parent.allExpanded[scope];
	const toggle = allToggle && (!ownToggle || allToggle.at > ownToggle.at) ? allToggle : ownToggle;
	const labelMatched = filtering && matchesQuery(text, parent.query);
	const visible = !filtering || parent.forceVisible || labelMatched || anyVisible;
	useEffect(() => parent.register(id, visible), [
		parent,
		id,
		visible
	]);
	const childContext = useMemo(() => ({
		query: parent.query,
		forceVisible: parent.forceVisible || labelMatched,
		filterDisabled: parent.filterDisabled,
		register: registerChild,
		close: parent.close,
		allExpanded: parent.allExpanded,
		setAllExpanded: parent.setAllExpanded,
		stamp: parent.stamp
	}), [
		parent,
		labelMatched,
		registerChild
	]);
	const isExpanded = !collapsible || (toggle ? toggle.expanded : filtering || defaultExpanded);
	const isEmpty = Children.count(children) === 0;
	return /* @__PURE__ */ jsxs("div", {
		className: cn("tw:flex tw:flex-col", className),
		style: visible ? void 0 : { display: "none" },
		children: [collapsible && isEmpty ? /* @__PURE__ */ jsxs("span", {
			"aria-disabled": true,
			className: cn(headerClass, "tw:text-muted-foreground", "tw:[&>svg:first-child]:opacity-50"),
			children: [/* @__PURE__ */ jsx(ChevronRight, {}), label]
		}) : collapsible && !isEmpty ? /* @__PURE__ */ jsxs("button", {
			type: "button",
			"aria-expanded": isExpanded,
			className: cn(headerClass, collapsibleHeaderClass),
			onClick: (event) => isToggleAllClick(event) ? parent.setAllExpanded(!isExpanded, scope) : setOwn((prev) => withToggle(prev, scope, {
				expanded: !isExpanded,
				at: parent.stamp()
			})),
			children: [/* @__PURE__ */ jsx(ToggleAllTooltip, {
				expanded: isExpanded,
				children: /* @__PURE__ */ jsx("span", {
					className: "tw:inline-flex",
					children: isExpanded ? /* @__PURE__ */ jsx(ChevronDown, {}) : /* @__PURE__ */ jsx(ChevronRight, {})
				})
			}), label]
		}) : /* @__PURE__ */ jsx("span", {
			className: headerClass,
			children: label
		}), /* @__PURE__ */ jsx(BreadcrumbDropdownContext, {
			value: childContext,
			children: collapsible && !isEmpty ? /* @__PURE__ */ jsx("div", {
				className: cn("tw:grid tw:transition-[grid-template-rows] tw:duration-200 tw:ease-out", "tw:motion-reduce:transition-none", isExpanded ? "tw:grid-rows-[1fr]" : "tw:grid-rows-[0fr]"),
				children: /* @__PURE__ */ jsx("div", {
					className: "tw:overflow-hidden",
					inert: !isExpanded,
					"aria-hidden": isExpanded ? void 0 : true,
					children: /* @__PURE__ */ jsx("div", {
						className: "tw:flex tw:flex-col tw:pl-6",
						children
					})
				})
			}) : /* @__PURE__ */ jsx("div", {
				className: "tw:flex tw:flex-col tw:pl-6",
				style: isExpanded ? void 0 : { display: "none" },
				children
			})
		})]
	});
};
// src/components/Breadcrumb/BreadcrumbDropdownItem.tsx
const itemClass = cn("tw:flex", "tw:w-full", "tw:items-center", "tw:gap-2", "tw:box-border", "tw:rounded-md", "tw:border-none", "tw:bg-transparent", "tw:px-2", "tw:py-1.5", "tw:cursor-pointer", "tw:text-left", "tw:text-sm", "tw:font-normal", "tw:text-foreground", "tw:no-underline", "tw:min-w-0", "tw:overflow-hidden", "tw:hover:bg-hover", "tw:data-[active]:bg-selected", "tw:data-[active]:font-medium", "tw:focus-visible:outline-hidden", "tw:focus-visible:ring-2", "tw:focus-visible:ring-ring/50", "tw:[&_svg:not([class*='tw:size-'])]:size-4", "tw:[&_svg]:shrink-0");
/**
* One selectable entity row inside `Breadcrumb.Dropdown`. Self-hides when it doesn't match the
* panel's filter query and closes the panel on select. Use `asChild` to render a router link.
*/
const BreadcrumbDropdownItem = ({ ref, asChild = false, textValue, active = false, closeOnSelect = true, className, children, onClick, ...props }) => {
	const context = use(BreadcrumbDropdownContext);
	if (!context) throw new Error("Breadcrumb.Dropdown.Item must be rendered inside Breadcrumb.Dropdown");
	const id = useId();
	const text = textValue ?? getNodeText(children);
	const visible = context.filterDisabled || !context.query || context.forceVisible || matchesQuery(text, context.query);
	useEffect(() => context.register(id, visible), [
		context,
		id,
		visible
	]);
	if (!visible) return null;
	return /* @__PURE__ */ jsx(asChild ? Slot : "button", {
		ref,
		...asChild ? {} : { type: "button" },
		"data-active": active || void 0,
		"aria-current": active || void 0,
		className: cn(itemClass, className),
		onClick: (event) => {
			onClick?.(event);
			if (closeOnSelect && !event.defaultPrevented) context.close();
		},
		...props,
		children
	});
};
// src/components/Breadcrumb/BreadcrumbLink.tsx
const BreadcrumbLink = ({ ref, asChild = false, className, ...props }) => {
	return /* @__PURE__ */ jsx(asChild ? Slot : "a", {
		ref,
		className: cn("tw:flex", "tw:items-center", "tw:gap-1.5", "tw:text-muted-foreground", "tw:font-normal", "tw:no-underline", "tw:hover:text-foreground", "tw:focus-visible:outline-hidden", "tw:focus-visible:ring-2", "tw:focus-visible:ring-ring/50", "tw:focus-visible:rounded-sm", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-3.5", "tw:transition-colors", className),
		...props
	});
};
// src/components/Breadcrumb/BreadcrumbDropdown.tsx
const chevronButtonClass = cn("tw:flex", "tw:items-center", "tw:justify-center", "tw:rounded-md", "tw:border-none", "tw:bg-transparent", "tw:cursor-pointer", "tw:size-5", "tw:text-muted-foreground", "tw:transition-colors", "tw:hover:bg-hover", "tw:hover:text-foreground", "tw:data-[state=open]:bg-hover", "tw:data-[state=open]:text-foreground", "tw:focus-visible:outline-hidden", "tw:focus-visible:ring-2", "tw:focus-visible:ring-ring/50", "tw:[&_svg]:transition-transform", "tw:[&[data-state=open]_svg]:rotate-180");
const plainLabelClass = cn("tw:flex", "tw:items-center", "tw:gap-1.5", "tw:text-muted-foreground", "tw:font-normal", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-3.5");
const PANEL_BASE = "tw:max-h-[min(640px,var(--radix-popover-content-available-height))]";
const PANEL_WIDTH = {
	small: "tw:w-[min(360px,var(--radix-popover-content-available-width))]",
	medium: "tw:w-[min(440px,var(--radix-popover-content-available-width))]",
	large: "tw:w-[min(560px,var(--radix-popover-content-available-width))]"
};
/**
* Breadcrumb segment opening a filterable switcher of sibling entities.
*
* - Use on the leftmost segment with siblings worth switching between; at most one per breadcrumb.
* - Not for row actions (`PageHeader.Detail` `overflowActions`) or a collapsed middle (`Ellipsis`).
* - Render inside `Breadcrumb.Item` in place of `Breadcrumb.Link`.
*/
const BreadcrumbDropdown = ({ label, splitTrigger = false, triggerLabel, open, defaultOpen = false, onOpenChange, size = "medium", filter = true, shouldFilter = true, searchValue, onSearchChange, searchPlaceholder = "Search…", emptyMessage = "No results.", panelClassName, children }) => {
	const searchRef = useRef(null);
	const [internalOpen, setInternalOpen] = useState(defaultOpen);
	const [internalQuery, setInternalQuery] = useState("");
	const stampRef = useRef(0);
	const stamp = useCallback(() => ++stampRef.current, []);
	const [allExpanded, setAllExpandedState] = useState(NO_TOGGLES);
	const setAllExpanded = useCallback((expanded, scope) => setAllExpandedState((prev) => withToggle(prev, scope, {
		expanded,
		at: stamp()
	})), [stamp]);
	const { register, anyVisible } = useMatchRegistry();
	const isOpen = open ?? internalOpen;
	const [wasOpen, setWasOpen] = useState(isOpen);
	if (wasOpen !== isOpen) {
		setWasOpen(isOpen);
		if (!isOpen) setAllExpandedState(NO_TOGGLES);
	}
	const handleOpenChange = useCallback((nextOpen) => {
		setInternalOpen(nextOpen);
		if (!nextOpen) setInternalQuery("");
		onOpenChange?.(nextOpen);
	}, [onOpenChange]);
	const close = useCallback(() => handleOpenChange(false), [handleOpenChange]);
	const effectiveTriggerLabel = triggerLabel ?? (typeof label === "string" ? `${label} menu` : "Open menu");
	const rawQuery = searchValue ?? internalQuery;
	const filterDisabled = !filter || !shouldFilter;
	const query = filterDisabled ? "" : normalizeQuery(rawQuery);
	const contextValue = useMemo(() => ({
		query,
		forceVisible: false,
		filterDisabled,
		register,
		close,
		allExpanded,
		setAllExpanded,
		stamp
	}), [
		query,
		filterDisabled,
		register,
		close,
		allExpanded,
		setAllExpanded,
		stamp
	]);
	return /* @__PURE__ */ jsxs(Popover, {
		open: isOpen,
		onOpenChange: handleOpenChange,
		children: [/* @__PURE__ */ jsxs("span", {
			className: "tw:flex tw:items-center tw:gap-0.5",
			children: [splitTrigger ? /* @__PURE__ */ jsx(BreadcrumbLink, {
				asChild: true,
				children: label
			}) : /* @__PURE__ */ jsx("span", {
				className: plainLabelClass,
				children: label
			}), /* @__PURE__ */ jsx(Popover.Trigger, {
				asChild: true,
				children: /* @__PURE__ */ jsx("button", {
					type: "button",
					"aria-label": effectiveTriggerLabel,
					className: chevronButtonClass,
					children: /* @__PURE__ */ jsx(ChevronDown, { className: "tw:size-3.5" })
				})
			})]
		}), /* @__PURE__ */ jsxs(Popover.Content, {
			align: "start",
			sideOffset: 8,
			collisionPadding: 16,
			"data-size": size,
			className: cn("tw:overflow-hidden tw:p-0", PANEL_BASE, PANEL_WIDTH[size], panelClassName),
			onOpenAutoFocus: filter ? (event) => {
				event.preventDefault();
				searchRef.current?.focus();
			} : void 0,
			children: [filter && /* @__PURE__ */ jsx("div", {
				"data-slot": "breadcrumb-dropdown-search",
				className: "tw:shrink-0 tw:border-0 tw:border-b tw:border-solid tw:border-border tw:p-2",
				children: /* @__PURE__ */ jsx(SearchInput, {
					ref: searchRef,
					value: rawQuery,
					onValueChange: (value) => {
						setInternalQuery(value);
						onSearchChange?.(value);
					},
					placeholder: searchPlaceholder,
					allowClear: true,
					disableDebounce: shouldFilter
				})
			}), /* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:flex-1 tw:flex-col tw:overflow-y-auto tw:p-2",
				children: [/* @__PURE__ */ jsx(BreadcrumbDropdownContext, {
					value: contextValue,
					children
				}), !filterDisabled && !!query && !anyVisible && /* @__PURE__ */ jsx("span", {
					className: "tw:px-2 tw:py-1.5 tw:text-sm tw:text-muted-foreground",
					children: emptyMessage
				})]
			})]
		})]
	});
};
BreadcrumbDropdown.Item = BreadcrumbDropdownItem;
BreadcrumbDropdown.Group = BreadcrumbDropdownGroup;
// src/components/Breadcrumb/BreadcrumbEllipsis.tsx
const BreadcrumbEllipsis = ({ ref, className, ...props }) => /* @__PURE__ */ jsxs("button", {
	ref,
	type: "button",
	className: cn("tw:flex", "tw:items-center", "tw:justify-center", "tw:rounded-md", "tw:border-none", "tw:bg-transparent", "tw:cursor-pointer", "tw:text-muted-foreground", "tw:transition-colors", "tw:hover:bg-hover", "tw:hover:text-foreground", "tw:data-[state=open]:bg-hover", "tw:data-[state=open]:text-foreground", "tw:focus-visible:outline-hidden", "tw:focus-visible:ring-2", "tw:focus-visible:ring-ring/50", "tw:p-1", className),
	...props,
	children: [/* @__PURE__ */ jsx(Ellipsis, { className: "tw:size-4" }), /* @__PURE__ */ jsx("span", {
		className: "tw:sr-only",
		children: "More"
	})]
});
// src/components/Breadcrumb/BreadcrumbItem.tsx
const BreadcrumbItem = ({ className, ...props }) => /* @__PURE__ */ jsx("li", {
	className: cn("tw:inline-flex", "tw:items-center", className),
	...props
});
// src/components/Breadcrumb/BreadcrumbList.tsx
const BreadcrumbList = ({ className, ...props }) => /* @__PURE__ */ jsx("ol", {
	className: cn("tw:flex", "tw:flex-wrap", "tw:items-center", "tw:gap-2", "tw:m-0", "tw:p-0", "tw:break-words", "tw:text-sm", "tw:text-muted-foreground", className),
	...props
});
// src/components/Breadcrumb/BreadcrumbPage.tsx
const BreadcrumbPage = ({ ref, className, ...props }) => /* @__PURE__ */ jsx("span", {
	ref,
	role: "link",
	"aria-disabled": "true",
	"aria-current": "page",
	className: cn("tw:font-medium", "tw:text-foreground", className),
	...props
});
// src/components/Breadcrumb/BreadcrumbSeparator.tsx
const BreadcrumbSeparator = ({ children, className, ...props }) => /* @__PURE__ */ jsx("li", {
	role: "presentation",
	"aria-hidden": "true",
	className: cn("tw:flex", "tw:items-center", "tw:justify-center", "tw:aspect-square", "tw:size-[15px]", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-2.5", "tw:text-muted-foreground", className),
	...props,
	children: children ?? /* @__PURE__ */ jsx("span", {
		className: "tw:select-none tw:text-sm tw:text-muted-foreground/50",
		children: "/"
	})
});
// src/components/Breadcrumb/Breadcrumb.tsx
const Breadcrumb = ({ className, ...props }) => /* @__PURE__ */ jsx("nav", {
	"aria-label": "breadcrumb",
	className: cn("tailwindcss-preflight", className),
	...props
});
Breadcrumb.List = BreadcrumbList;
Breadcrumb.Item = BreadcrumbItem;
Breadcrumb.Link = BreadcrumbLink;
Breadcrumb.Page = BreadcrumbPage;
Breadcrumb.Separator = BreadcrumbSeparator;
Breadcrumb.Ellipsis = BreadcrumbEllipsis;
Breadcrumb.Dropdown = BreadcrumbDropdown;
export { Breadcrumb };

//# sourceMappingURL=Breadcrumb-B_4zm7jJ.js.map